import React, { useEffect } from 'react';
import classNames from 'classnames';
import { Link } from 'react-router-dom';
import Router from 'src/navigation/router';
import { routes } from 'src/navigation';
import styles from '../../assets/provider-tab.module.scss';
import InfoItem from 'src/components/InfoItem';
import { IProvider } from 'src/types/provider';
import { QueryParams } from 'src/types/grid';
import RenderFile from 'src/components/RenderFile';
import user from '../../../../../../../../services/user-auth';
import UserRole from '../../../../../../../../enumerables/user-role';
import { changeToZeroMoment } from '../../../../../../../../helpers/timeHelpers';
import { numberWithCommas } from '../../../../../../../../helpers/format';
import InfoCaseRateItem from '../../../../../../../../components/InfoCaseRateItem';
import Loader from '../../../../../../../../components/Loader';
import { FieldInput } from '../../../../../../../../components/redux-form/fields/FieldInput';
import { FieldInputMask } from '../../../../../../../../components/redux-form/fields/FieldInputMask';
import { FieldMultiDropdown } from '../../../../../../../../components/redux-form/fields/FieldMultiDropdown';
import { FieldDropdown } from '../../../../../../../../components/redux-form/fields/FieldDropdown';

export interface StateProps {
    serviceTypes: any;
    files: any;
    serviceLocations: any;
    cases: any;
    inProgress: boolean;
    exigentBonusStatus: any;
    exigentBonusStatusInProgress: boolean;
}
export interface DispatchProps {
    getFiles: (id: string | number) => void;
    getServiceTypes: (params: QueryParams) => void;
    getServiceLocations: (params: QueryParams) => void;
    getCases: (params: QueryParams) => void;
    getExigentBonusStatus: (id: string | number) => void;
    resetExigentBonusStatus: () => void;
}

export interface OwnProps {
    provider: Partial<IProvider> | null;
    tab?: string;
}

export interface Props extends StateProps, DispatchProps, OwnProps {}
const ProviderInfo: React.FunctionComponent<Props> = ({
    serviceTypes,
    getServiceLocations,
    getServiceTypes,
    getCases,
    provider,
    getFiles,
    tab,
    files,
    serviceLocations,
    cases,
    inProgress,
    exigentBonusStatus,
    exigentBonusStatusInProgress,
    getExigentBonusStatus,
    resetExigentBonusStatus,
}: Props) => {
    useEffect(() => {
        if (tab === 'main-info') {
            getServiceLocations({
                page: 1,
                itemsPerPage: 9000,
            });
            getServiceTypes({
                page: 1,
                itemsPerPage: 9000,
            });

            if (provider && provider.id) {
                getFiles(provider.id);
                getCases({
                    page: 1,
                    filters: {
                        'occasionProviders.provider': provider.id,
                    },
                    itemsPerPage: 9000,
                });
            }
        }

        // eslint-disable-next-line
    }, [getServiceLocations, tab, getServiceTypes, getFiles]);

    useEffect(() => {
        resetExigentBonusStatus();

        return () => resetExigentBonusStatus();
        // eslint-disable-next-line
    }, [provider?.id]);

    if (!provider) {
        return null;
    }

    const {
        id,
        email,
        fullName,
        employeeId,
        phone,
        npi,
        licenseDescription,
        createdAt,
        userServiceTypes,
        userServiceLocations,
        rateAdminWorks,
        staffTitle,
        license,
        isGetAdvance,
        isTest,
        isReferred,
        referredBy,
        referredAmount,
        referredHours,
        annualSalary,
        certificates,
        status,
        supervisor,
    } = provider;

    let serviceTypesNames: any = [];

    if (userServiceTypes) {
        userServiceTypes.forEach((it: any) => {
            serviceTypesNames.push(serviceTypes[it.serviceType.id]);
        });
    }

    serviceTypesNames = serviceTypesNames.join(', ');
    let serviceLocationsNames: any = [];
    if (userServiceLocations) {
        userServiceLocations.forEach((it: any) => {
            serviceLocationsNames.push(serviceLocations[it.serviceLocation.id]);
        });
    }
    serviceLocationsNames = serviceLocationsNames.join(', ');

    let previewSupervisors = null;

    if (user.getRoles().includes(UserRole.ROLE_ADMIN)) {
        previewSupervisors =
            supervisor &&
            supervisor[0] &&
            supervisor.map((s: any) => (
                <span key={s.fullName} style={{ paddingRight: '20px' }}>
                    <a
                        key={s.fullName}
                        rel="noopener noreferrer"
                        target="_blank"
                        href={`${Router.generate(routes.PROVIDER_DETAIL, { id: s.id, tab: 'main-info', subId: '' })}`}
                    >
                        {s.fullName}
                    </a>
                </span>
            ));
    } else {
        previewSupervisors =
            supervisor &&
            supervisor[0] &&
            supervisor.map((s: any) => (
                <span key={s.fullName} style={{ paddingRight: '20px' }}>
                    {s.fullName}
                </span>
            ));
    }

    const previewFiles =
        files &&
        files.map((file: any) => (
            <li style={{ listStyleType: 'none' }} key={file.attach}>
                <a rel="noopener noreferrer" target="_blank" href={`${file.attach}`}>
                    <RenderFile file={file} />
                </a>
            </li>
        ));

    const providerStatuses: any = user.getUserAvailableStatus();
    let statusName = '';
    Object.keys(providerStatuses).forEach((it: any) => {
        if (status === providerStatuses[it]) {
            statusName = `${it}`;
        }
    });

    return (
        <div className={classNames(styles.tabWrap)}>
            {inProgress ? <Loader /> : null}
            <div className={classNames(styles.infoItems)}>
                <div className={classNames(styles.box)}>
                    <div className={classNames(styles.title)}>Contact Information</div>
                    <div>
                        <InfoItem title={'Full Name:'} value={fullName} />
                        <InfoItem title={'Email:'} value={email} />
                        <InfoItem title={'Employee ID:'} value={employeeId} />
                        <InfoItem
                            title={'Phone Number:'}
                            value={
                                <a rel="noopener noreferrer" target="_blank" href={`tel:${phone}`}>
                                    {phone}
                                </a>
                            }
                        />
                    </div>
                </div>

                <div className={classNames(styles.box, styles.staffBox)}>
                    <div className={classNames(styles.title)}>Staff Information</div>
                    <div>
                        <InfoItem title={'NPI:'} value={npi} />
                        <InfoItem title={'License Number:'} value={license} />
                        <InfoItem title={'License Description:'} value={licenseDescription} />
                        <InfoItem title={'Staff Title:'} value={staffTitle} />
                        <InfoItem title={'Certificates:'} value={certificates} />
                    </div>
                </div>

                <div className={classNames(styles.box, styles.serviceBox)}>
                    <div className={classNames(styles.title)}>Service Information</div>
                    <div>
                        <InfoItem title={'Service Locations:'} value={`${serviceLocationsNames}`} />
                        <InfoItem title={'Service Types:'} value={`${serviceTypesNames}`} />
                    </div>
                </div>

                <div className={classNames(styles.box, styles.supervisionBox)}>
                    <div className={classNames(styles.title)}>Supervision Information</div>
                    <div>
                        <InfoItem title={'Is the provider created for test:'} value={isTest ? 'Yes' : 'No'} />
                        <InfoItem title={'Supervisor:'} value={previewSupervisors} />
                    </div>
                </div>

                <div className={classNames(styles.box, styles.bonusBox)}>
                    <div className={classNames(styles.title)}>Referral Bonus Information</div>
                    <div>
                        <InfoItem title={'Is the provider referred?:'} value={isReferred ? 'Yes' : 'No'} />
                        {isReferred && <InfoItem title={'Referral provider:'} value={referredBy?.fullName} />}
                        {isReferred && <InfoItem title={'Referral bonus amount:'} value={referredAmount} />}
                        {isReferred && <InfoItem title={'Number of needed billed hours:'} value={referredHours} />}
                    </div>

                    <div className={classNames(styles.title)}>Exigent Bonus Information</div>
                    <div>
                        <button
                            type="button"
                            className={classNames('btn', 'btn-outline-secondary', styles.exigentBonusStatusBtn)}
                            disabled={exigentBonusStatusInProgress}
                            onClick={() => id && getExigentBonusStatus(id)}
                        >
                            {exigentBonusStatusInProgress ? 'Loading...' : 'Get Status'}
                        </button>
                        {exigentBonusStatus && exigentBonusStatus.statuses.length === 0 && (
                            <InfoItem title={''} value={'No exigent bonus data found.'} />
                        )}
                        {exigentBonusStatus &&
                            exigentBonusStatus.statuses.map((item: any, k: number) => {
                                const caseNumberPrefix = item.caseNumber ? `Case number #${item.caseNumber}: ` : '';

                                return (
                                    <InfoItem
                                        key={k}
                                        title={''}
                                        value={`${caseNumberPrefix}${item.hoursBilled}/${item.hoursRequired} hrs for the next bonus`}
                                    />
                                );
                            })}
                    </div>
                </div>

                <div className={classNames(styles.box, styles.salaryBox)}>
                    <div className={classNames(styles.title)}>Salary Information</div>
                    <div>
                        <InfoItem
                            title={'Annual Salary, $:'}
                            value={annualSalary ? `$${numberWithCommas(annualSalary)}` : ''}
                        />
                        <InfoItem title={'Gets an Advance:'} value={isGetAdvance ? 'Yes' : 'No'} />
                        <div className={classNames(styles.blockInfo)}>
                            <b className={classNames(styles.infoTitle)}>Rate for Admin Work per Hour, $</b>
                            <div className={classNames(styles.valueBlock)}>
                                {rateAdminWorks.map((itW: any, k: number) => (
                                    <div key={k}>
                                        <InfoItem
                                            title={''}
                                            value={`$${numberWithCommas(itW.rateForAdminWork)} -  ${
                                                itW.createdAt ? changeToZeroMoment(itW.createdAt, 'MMM D, YYYY') : null
                                            }`}
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className={classNames(styles.blockInfo)}>
                            <b className={classNames(styles.infoTitle)}>Cases with Internal Rate per Hour, $</b>
                            <div className={classNames(styles.valueBlock)}>
                                {cases?.map((itW: any, k: number) => {
                                    let rate = '';
                                    itW?.occasionInternalRates?.map((itR: any, k: number) => {
                                        rate =
                                            rate +
                                            '$' +
                                            itR?.internalRatePerHour +
                                            ' - ' +
                                            (itR.createdAt ? changeToZeroMoment(itR.createdAt, 'MMM D, YYYY') : '') +
                                            '; ';
                                    });

                                    return (
                                        <div key={k}>
                                            <InfoCaseRateItem
                                                title={''}
                                                value={` ${rate}`}
                                                linkTitle={itW?.id}
                                                linkHref={Router.generate(routes.CASE_DETAIL, { id: itW?.id })}
                                            />
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>

                <InfoItem title={'Creation Date:'} value={changeToZeroMoment(createdAt, 'MMM D, YYYY')} />
                <InfoItem title={'Status:'} value={statusName} />

                <InfoItem title={'Documents:'} value={previewFiles} />
            </div>

            <div>
                {user.getRoles().includes(UserRole.ROLE_ADMIN) ? (
                    <Link
                        className={classNames('btn', 'btn-outline-secondary')}
                        to={Router.generate(routes.PROVIDER_EDIT, { id })}
                    >
                        EDIT
                    </Link>
                ) : null}
            </div>
        </div>
    );
};

export default ProviderInfo;
